/**
 * BackLink — a back control that is a REAL ANCHOR first and a history pop
 * second (the LET-83 shape).
 *
 * ## The two answers, and why both are needed
 *
 * A standard mobile back POPS the stack: the reader came from somewhere and
 * expects to land back there, scroll position and all. But a deep link opened
 * COLD has no stack — the entry the pop would take is the referring site, or
 * nothing — and "back" must then mean the surface's logical PARENT. Two
 * different questions, so two different sources:
 *
 *   * the `href` is the DECLARED parent (`back:` in `web/manifests/routes.yaml`,
 *     emitted as `handle.backTo`, or a loader's `backHref` where the parent
 *     depends on data). It is a `Route`, so it cannot be a string someone
 *     typed, and it renders into the DOM on the server;
 *   * `onActivate` pops instead, but only when there IS something to pop.
 *
 * ## How "is there a stack?" is answered
 *
 * `window.history.state?.idx` — react-router's own index into the history
 * entries IT created. `0` means this document is the first entry of the SPA's
 * history, i.e. the reader arrived here directly; `> 0` means the app itself
 * navigated here and `navigate(-1)` lands inside the app. `history.length` is
 * NOT the same question — it counts the whole tab's history, including pages
 * from other origins, so it is `> 1` on a cold deep link from a search result
 * and would send the reader off-site.
 *
 * ## Why the anchor comes first
 *
 * Pre-hydration, and with JS disabled, the control is a link to a real page
 * rather than a dead button — the same reason the hub's back control stopped
 * being a `Button onClick` that pushed a URL existing only in JS.
 * `EnhancedLink` lets a modifier-click, a middle-click and a right-click
 * through to the browser untouched (`isBrowserManagedClick`), so "open the
 * parent in a new tab" keeps working.
 *
 * ## Why it is an `entity` and not chrome
 *
 * `MobileTopBar` (`infra`) mounts it, and so does the browse canvas's own
 * `ListingsTopBar` (`feature`) — and `feature -> infra` is refused by the
 * element DAG, deliberately: a feature may not compose page chrome. A back
 * control is not chrome, it is a shared control that chrome uses, so it lives
 * beside `Breadcrumbs` in `shared/components/` where both layers may reach it.
 * That is also what makes "the hub's back is a real back" the same code as
 * every other page's rather than a second implementation of the same rule.
 */
import { EnhancedLink, useRouter } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { Route } from '@letsgethome/routes';
import { cn } from '@letsgethome/ui/lib/utils';
import { ArrowLeft } from 'lucide-react';

/**
 * Does the app's own history hold an entry to go back to?
 *
 * Exported so the test can state the two cases without reaching into
 * `window` from the component's own module.
 */
export function hasAppHistory(): boolean {
  if (typeof window === 'undefined') return false;
  const state: unknown = window.history.state;
  if (typeof state !== 'object' || state === null || !('idx' in state)) return false;
  const { idx } = state as { idx: unknown };
  return typeof idx === 'number' && idx > 0;
}

export interface BackLinkProps {
  /** The DECLARED parent — where a cold back goes. */
  readonly backTo: Route;
  /** Extra classes for the control's box (the band decides its size). */
  readonly className?: string;
}

export function BackLink({ backTo, className }: BackLinkProps) {
  const t = useTranslations('common');
  // The SEAM's router, not react-router's `useNavigate` — `no-raw-router-link`
  // reserves the raw APIs for `@letsgethome/router-i18n` itself, and a pop is
  // locale-invariant only because the seam owns the prefix in the first place.
  const router = useRouter();

  return (
    <EnhancedLink
      href={backTo}
      aria-label={t('back')}
      data-slot="back-link"
      className={cn(
        'inline-flex h-10 w-10 items-center justify-center rounded-full',
        'text-foreground transition-colors hover:bg-accent',
        className
      )}
      onActivate={
        // `undefined`, not a no-op: `EnhancedLink` only `preventDefault()`s
        // when an activation handler is present, so a page with no app
        // history keeps the anchor's own navigation rather than having it
        // cancelled and re-issued.
        hasAppHistory()
          ? () => {
              // `void`: a pop has no destination to await, and the seam's
              // navigate returns the router's own promise.
              void router.back();
            }
          : undefined
      }
    >
      <ArrowLeft className="h-5 w-5" />
    </EnhancedLink>
  );
}
